import { clearAuthSession, getAuthToken } from "./auth";

export interface RequestError extends Error {
  response?: Response;
  data?: unknown;
  status?: number;
}

async function parseJSON(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function handleUnauthorized(): void {
  clearAuthSession();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

async function checkStatus(
  response: Response,
  hadToken: boolean,
): Promise<Response> {
  if (response.ok) return response;

  if (response.status === 401 && hadToken) {
    handleUnauthorized();
  }

  const data = await parseJSON(response);
  const error: RequestError = new Error(response.statusText);
  error.response = response;
  error.status = response.status;
  error.data = data;
  throw error;
}

export default async function request<T = unknown>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAuthToken();
  const headers = new Headers(options.headers);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(url, { ...options, headers });
  const checked = await checkStatus(response, token !== null);
  return (await parseJSON(checked)) as T;
}
